// src/app/app.js

import { get, set } from "core/idb-keyval.js";
import { LayoutManager } from "core/layout-manager.js";
import { MediaLoader } from "core/media-loader.js";
import { SettingsStore } from "core/settings-store.js";
import { SlideshowState } from "core/slideshow-state.js";

// sessionStorage key set by main.js right before a SW-update-triggered reload.
const SW_UPDATE_RELOAD_KEY = "slidey-sw-update-reload";
// Per-tab id so each browser tab remembers its own last loaded directory.
const TAB_ID_KEY = "slidey-tab-id";

export class App {
  constructor(root) {
    this.root = root;

    this.loader = new MediaLoader();
    this.state = new SlideshowState();
    this.layouts = new LayoutManager();
    this.settings = new SettingsStore();

    this.modal = root.querySelector("modal-reload");
    this.toolbar = root.querySelector("slideshow-toolbar");
    this.slideshow = root.querySelector("slideshow-view");
    this.thumbs = root.querySelector("thumbnail-strip");
    this.intro = root.querySelector("intro-overlay");
    this.loading = root.querySelector("loading-overlay");

    this.timer = null;

    // Auto-hide UI
    this.uiVisible = true;
    this._uiTimer = null;
    this._onUserActivity = this._onUserActivity.bind(this);

    // Restore settings
    this.state.intervalSeconds = this.settings.intervalSeconds;
    this.state.layoutId = this.settings.layoutId;
    this.state.fit = this.settings.fit;
    this.state.includeSubdirs = this.settings.includeSubdirs;

    this.slideshow.state = this.state;
    this.slideshow.setFitMode(this.state.fit);
    this.slideshow.setLayout(
      this.layouts.getById(this.state.currentLayoutId).dsl,
    );

    this.toolbar.setLayoutName(this.layouts.getName(this.state.layoutId));

    this._wireToolbar();
    this._wireState();
    this._wireThumbnails();

    this._updateThumbnails();
    this._updateSlideshow();

    // Start intro animation
    this.intro.show();

    // Ask to reload last directory
    this._maybePromptReload();

    // Global activity listeners
    window.addEventListener("mousemove", this._onUserActivity, {
      passive: true,
    });
    window.addEventListener("touchstart", this._onUserActivity, {
      passive: true,
    });
    window.addEventListener("touchmove", this._onUserActivity, {
      passive: true,
    });

    // Any toolbar interaction should reset the auto-hide timer
    this.toolbar.addEventListener("click", this._onUserActivity);

    // Fullscreen listener
    document.addEventListener("fullscreenchange", this._onFullscreenChange);
  }

  destroy() {
    // Manual cleanup since this is not a custom element
    window.removeEventListener("mousemove", this._onUserActivity);
    window.removeEventListener("touchstart", this._onUserActivity);
    window.removeEventListener("touchmove", this._onUserActivity);
    this.toolbar.removeEventListener("click", this._onUserActivity);
    document.removeEventListener("fullscreenchange", this._onFullscreenChange);
  }

  // ------------------------------------------------------------
  // Fullscreen
  // ------------------------------------------------------------
  _onFullscreenChange = () => {
    const isFullscreen = !!document.fullscreenElement;

    if (!isFullscreen) {
      this._onExitFullscreen();
    } else {
      this._onEnterFullscreen();
    }
  };

  _onEnterFullscreen() {
    this.isFullscreen = true;
  }

  _onExitFullscreen() {
    this.isFullscreen = false;
  }

  // ------------------------------------------------------------
  // Directory Picker
  // ------------------------------------------------------------
  _pickDirectory = async () => {
    try {
      const dir = await this.loader.pickDirectory();
      await this._loadDirectory(dir);

      await navigator.storage.persist();
      this.saveDirectoryHandle(dir);

      this.loading.active = false;
    } catch (err) {
      console.error(err);
    }
  };

  // ------------------------------------------------------------
  // Toolbar
  // ------------------------------------------------------------
  _wireToolbar() {
    this.toolbar.addEventListener("reset-slideshow", () => {
      this._stopAutoplay();
      this.slideshow.setLayoutAndSlice(
        this.layouts.getById(this.state.currentLayoutId).dsl,
        [],
      );
      this.state.setMedia([]);
    });

    this.toolbar.addEventListener("pick-directory", () => {
      this._pickDirectory();
    });

    this.toolbar.addEventListener("toggle-fit", () => {
      this.state.toggleFit();
      this.slideshow.setFitMode(this.state.fit);
      this.settings.fit = this.state.fit;
    });

    this.toolbar.addEventListener("shuffle", (e) => {
      const autoShuffle = e.detail;
      this.settings.autoShuffle = autoShuffle;
      this.state.shuffleMedia();
    });

    this.toolbar.addEventListener("toggle-auto-play", (e) => {
      const autoPlay = e.detail;
      this.settings.autoPlay = autoPlay;
    });

    this.toolbar.addEventListener("toggle-subdirs", () => {
      this.state.toggleIncludeSubdirs();
      this.settings.includeSubdirs = this.state.includeSubdirs;
    });

    this.toolbar.addEventListener("next", () => {
      this.state.next();
      this._restartAutoplay();
    });

    this.toolbar.addEventListener("prev", () => {
      this.state.prev();
      this._restartAutoplay();
    });

    this.toolbar.addEventListener("cycle-layout", () => {
      const current = this.state.currentLayoutId || "single";
      const next = this.layouts.nextLayoutId(current);
      this.state.setCurrentLayoutId(next);
      this.settings.layoutId = next;
      this.toolbar.setLayoutName(this.layouts.getName(next));
    });

    this.toolbar.addEventListener("toggle-play", (e) => {
      const playing = e.detail;
      if (playing) {
        this._startAutoplay();
      } else {
        this._stopAutoplay();
      }
    });

    this.toolbar.addEventListener("transition-style-change", (e) => {
      this.slideshow.transitionStyle = e.detail;
    });

    this.toolbar.addEventListener("change-transition-time", (e) => {
      const seconds = e.detail;
      this.state.setTransitionTime(seconds);
      this._restartAutoplay();
    });

    this.modal.addEventListener("cancel", () => {
      this.modal.open = false;
      this.saveDirectoryHandle(null);
    });

    this.modal.addEventListener("confirm", () => {
      this.modal.open = false;
      this._reloadLastDirectory();
    });

    // Pause autoplay when zooming
    this._wasPlaying = false;
    this._isZoomed = false;

    this.slideshow.addEventListener("zoom-open", () => {
      this._isZoomed = true;
      this._wasPlaying = !!this.timer;
      this._stopAutoplay();
      this._showUi(false);
    });

    this.slideshow.addEventListener("zoom-close", () => {
      this._isZoomed = false;
      if (this._wasPlaying) {
        this._startAutoplay();
      }
    });

    // Slideshow toggles playback
    this.slideshow.addEventListener("toggle-playback", () => {
      if (this.timer) {
        this._stopAutoplay();
        this.toolbar.playing = false;
      } else {
        this._startAutoplay();
        this.toolbar.playing = true;
      }
    });

    // Layout switching
    this.slideshow.addEventListener("next-layout", () => {
      const current = this.state.currentLayoutId || "single";
      const next = this.layouts.nextLayoutId(current);
      this.state.setCurrentLayoutId(next);
      this.settings.layoutId = next;
      this.toolbar.setLayoutName(this.layouts.getName(next));
    });

    this.slideshow.addEventListener("prev-layout", () => {
      const current = this.state.currentLayoutId || "single";
      const prev = this.layouts.previousLayoutId(current);
      this.state.setCurrentLayoutId(prev);
      this.settings.layoutId = prev;
      this.toolbar.setLayoutName(this.layouts.getName(prev));
    });

    // Navigation
    this.slideshow.addEventListener("next", () => {
      this.state.next();
      this._restartAutoplay();
    });
    this.slideshow.addEventListener("prev", () => {
      this.state.prev();
      this._restartAutoplay();
    });

    // Fullscreen
    this.slideshow.addEventListener("toggle-fullscreen", () => {
      if (!document.fullscreenElement) {
        this.root.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    });

    // Folder picker
    this.slideshow.addEventListener("open-folder", () => {
      this._pickDirectory();
    });
  }

  // ------------------------------------------------------------
  // State
  // ------------------------------------------------------------
  _wireState() {
    this.state.addEventListener("change", () => {
      this._updateSlideshow();
      this._updateThumbnails();
      this.toolbar.transitionTime = this.state.transitionTime;
    });
  }

  // ------------------------------------------------------------
  // Thumbnails
  // ------------------------------------------------------------
  _wireThumbnails() {
    this.thumbs.addEventListener("select-index", (e) => {
      this.state.setIndex(e.detail);
    });
  }

  // ------------------------------------------------------------
  // Autoplay
  // ------------------------------------------------------------
  _startAutoplay() {
    this._stopAutoplay();
    this.timer = setInterval(() => {
      this.state.next();
    }, this.state.transitionTime * 1000);

    this._startUiHideTimer();
  }

  _stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.uiVisible = true;
    this._applyUiVisibility();
  }

  _restartAutoplay() {
    if (this.timer) {
      this._startAutoplay();
    }
  }

  // ------------------------------------------------------------
  // UI Auto-Hide
  // ------------------------------------------------------------

  _showUi(visible) {
    this.uiVisible = visible;
    this._applyUiVisibility();
  }

  _onUserActivity() {
    if (!this._isZoomed) {
      if (!this.uiVisible) {
        this._showUi(true);
      }
      this._startUiHideTimer();
    }
  }

  _startUiHideTimer() {
    clearTimeout(this._uiTimer);

    if (!this.timer) return;

    this._uiTimer = setTimeout(() => {
      if (this.timer) {
        this._showUi(false);
      }
    }, 8000);
  }

  _applyUiVisibility() {
    if (this.uiVisible) {
      document.documentElement.classList.remove("ui-hidden");
      this.toolbar.classList.remove("ui-hidden");
      this.thumbs.classList.remove("ui-hidden");
      this.root.classList.remove("ui-hidden");
    } else {
      document.documentElement.classList.add("ui-hidden");

      this.toolbar.classList.add("ui-hidden");
      this.thumbs.classList.add("ui-hidden");
      this.root.classList.add("ui-hidden");
    }
  }

  // ------------------------------------------------------------
  // UI updates
  // ------------------------------------------------------------
  _updateThumbnails() {
    this.thumbs.images = this.state.media;
    this.thumbs.selectedIndex = this.state.index;
  }

  _updateSlideshow() {
    if (!this.state.media.length) return;

    if (!this.state.currentLayoutId) {
      const count = Math.min(this.state.media.length, 4);
      let id = "single";
      if (count === 2) id = "two-side";
      if (count === 3) id = "one-plus-two";
      if (count >= 4) id = "four-grid";
      this.state.currentLayoutId = id;
    }

    const slice = this.state.getSlice();
    const layout = this.layouts.getById(this.state.currentLayoutId).dsl;

    this.slideshow.setLayoutAndSlice(layout, slice);
  }

  // ------------------------------------------------------------
  // Reload last directory
  // ------------------------------------------------------------
  _getTabId() {
    let id = sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  }

  _dirHandleKey() {
    return `slideshow-dir-handle:${this._getTabId()}`;
  }

  async saveDirectoryHandle(handle) {
    try {
      await navigator.storage.persist();
      await set(this._dirHandleKey(), handle);
    } catch (err) {
      console.error("Failed to save directory handle", err);
    }
  }

  async loadDirectoryHandle() {
    try {
      return await get(this._dirHandleKey());
    } catch (err) {
      console.error("Failed to load directory handle", err);
      return null;
    }
  }

  async hasPermission(handle) {
    try {
      return await handle.queryPermission({ mode: "read" });
    } catch (err) {
      console.warn("Opera crashed during queryPermission", err);
      return "denied";
    }
  }

  async requestPermission(handle) {
    try {
      return await handle.requestPermission({ mode: "read" });
    } catch (err) {
      console.warn("Opera crashed during requestPermission", err);
      return "denied";
    }
  }

  async _loadDirectory(handle) {
    let hasPerm = await this.hasPermission(handle);
    if (!hasPerm) {
      hasPerm = await this.requestPermission(handle);
      if (!hasPerm) {
        console.warn("Permission denied for directory");
        return;
      }
    }

    this.loading.filesFound = 0;
    this.loading.active = true;

    const images = await this.loader.loadFromDirectory(
      handle,
      this.settings.includeSubdirs,
      () => {
        this.loading.filesFound += 1;
      },
    );

    this.state.setMedia(images);
    if (this.settings.autoShuffle) {
      this.state.shuffleMedia();
    }

    this._logMediaStats(images);

    this.loading.active = false;

    if (this.settings.autoPlay) {
      this._startAutoplay();
      this.toolbar.playing = true;
    }
  }

  _logMediaStats(media) {
    const counts = {};
    for (const item of media) {
      const mime = item.file?.type || "unknown";
      counts[mime] = (counts[mime] || 0) + 1;
    }

    const stats = Object.entries(counts).map(([type, count]) => ({
      type,
      count,
    }));

    console.log(`Loaded ${media.length} file(s) by type:`);
    console.table(stats);
  }

  _showReloadModal(handle) {
    this.modal.directoryName = handle.name;
    this.modal.open = true;
  }

  async _maybePromptReload() {
    const handle = await this.loadDirectoryHandle();

    if (!handle) return;

    // Skip the confirmation prompt when this reload was triggered by a
    // service-worker update — just restore what was already showing.
    if (sessionStorage.getItem(SW_UPDATE_RELOAD_KEY)) {
      sessionStorage.removeItem(SW_UPDATE_RELOAD_KEY);
      try {
        await this._loadDirectory(handle);
      } catch (err) {
        console.warn("Failed to auto-reload directory after update.", err);
      }
      return;
    }

    const granted = await this.hasPermission(handle);

    if (granted) {
      this._showReloadModal(handle);
    } else {
      const promptable =
        (await handle.queryPermission({ mode: "read" })) === "prompt";
      if (promptable) {
        this._showReloadModal(handle);
      }
    }
  }

  async _reloadLastDirectory() {
    const handle = await this.loadDirectoryHandle();
    if (!handle) return;

    try {
      await this._loadDirectory(handle);
    } catch (err) {
      console.warn("Failed to reload last directory.");
    }
  }
}
